import type {
  CronFlavor,
  Locale,
  ToolId,
  ToolModeId,
  TransformErrorCode,
  UrlActionId
} from "./types";

interface ToolCopy {
  title: string;
}

interface ModeCopy {
  title: string;
  summary: string;
  actionLabel: string;
}

interface LocalePack {
  toolsLabel: string;
  modesLabel: string;
  urlEncodeModesLabel: string;
  feedbackContactLabel: string;
  officialLinks: {
    justOneApi: {
      title: string;
      summary: string;
    };
    justSerpApi: {
      title: string;
      summary: string;
    };
  };
  clearLabel: string;
  copyLabel: string;
  actionFeedback: {
    cleared: string;
    copied: string;
    nothingToCopy: string;
    copyFailed: string;
  };
  outputPlaceholder: string;
  statusIdle: string;
  statusRunning: (actionLabel: string) => string;
  errorMessages: Record<TransformErrorCode, string>;
  tools: Record<ToolId, ToolCopy>;
  toolInputPlaceholders: Record<ToolId, string>;
  modeInputPlaceholders: Partial<Record<ToolModeId, string>>;
  secondaryInputPlaceholders: Partial<Record<ToolModeId, string>>;
  urlActions: Record<UrlActionId, string>;
  timestampTool: {
    liveTitle: string;
    liveSummary: string;
    secondsLabel: string;
    millisecondsLabel: string;
    localTimeLabel: string;
    utcTimeLabel: string;
    timezoneLabel: string;
    fromTimestampTitle: string;
    fromTimestampSummary: string;
    toTimestampTitle: string;
    toTimestampSummary: string;
    fillCurrentLabel: string;
    fillNowLabel: string;
    datetimePlaceholder: string;
    unitAuto: string;
    unitSeconds: string;
    unitMilliseconds: string;
    emptyHint: string;
  };
  cronTool: {
    title: string;
    summary: string;
    typeLabel: string;
    expressionLabel: string;
    currentTimezoneLabel: string;
    fieldGuideLabel: string;
    exampleLabel: string;
    descriptionLabel: string;
    nextRunsLabel: string;
    emptyHint: string;
    flavors: Record<
      CronFlavor,
      {
        title: string;
        fieldGuide: string;
        example: string;
        hint: string;
      }
    >;
  };
  timestampResult: {
    detectedType: string;
    seconds: string;
    milliseconds: string;
    isoUtc: string;
    localTime: string;
    detectedTypes: Record<"seconds" | "milliseconds" | "datetime", string>;
  };
  textDiff: {
    originalLabel: string;
    modifiedLabel: string;
    resultLabel: string;
    summary: (summary: { added: number; removed: number; unchanged: number }) => string;
    empty: string;
  };
  jsonTree: {
    objectCount: (count: number) => string;
    arrayCount: (count: number) => string;
    emptyObject: string;
    emptyArray: string;
  };
  modes: Record<ToolModeId, ModeCopy>;
}

export const localeLabels: Record<Locale, string> = {
  zh: "中文",
  en: "EN"
};

export const localePacks: Record<Locale, LocalePack> = {
  zh: {
    toolsLabel: "工具",
    modesLabel: "模式",
    urlEncodeModesLabel: "编码模式",
    feedbackContactLabel: "反馈邮箱",
    officialLinks: {
      justOneApi: {
        title: "Just One API",
        summary: "Social Media & Ecommerce APIs"
      },
      justSerpApi: {
        title: "Just Serp API",
        summary: "Google Search API"
      }
    },
    clearLabel: "清空",
    copyLabel: "复制",
    actionFeedback: {
      cleared: "已清空",
      copied: "已复制到剪贴板",
      nothingToCopy: "没有可复制的内容",
      copyFailed: "复制失败"
    },
    outputPlaceholder: "运行后，结果会在这里输出。",
    statusIdle: "选择工具和模式后即可开始，所有处理都在浏览器本地完成。",
    statusRunning: (actionLabel) => `正在执行：${actionLabel}`,
    errorMessages: {
      INVALID_JSON: "输入不是合法 JSON，请检查括号、引号和逗号。",
      INVALID_ESCAPED_JSON: "输入不是合法的 JSON 转义文本。",
      INVALID_CRON_EXPRESSION: "输入不是合法的 Cron 表达式。",
      INVALID_CURL_COMMAND: "输入不是合法的 cURL 命令。",
      INVALID_JWT: "输入不是合法的 JWT。",
      INVALID_TIMESTAMP: "输入不是合法的时间戳或日期时间。",
      INVALID_TEXT_ENCODED_TEXT: "输入不是合法的 Unicode 转义文本。",
      INVALID_HEX_TEXT: "输入不是合法的 Hex 文本。",
      INVALID_BASE64_TEXT: "输入不是合法的 Base64 文本。",
      INVALID_BASE64_IMAGE: "输入不是合法的 Base64 图片文本。",
      INVALID_URL_ENCODED_TEXT: "输入不是合法的 URL 编码文本。",
      UNKNOWN: "处理失败，请检查输入内容。"
    },
    tools: {
      json: {
        title: "JSON解析"
      },
      "url-encode": {
        title: "URL编解码"
      },
      "text-codec": {
        title: "文本编解码"
      },
      base64: {
        title: "Base64编解码"
      },
      html: {
        title: "HTML处理"
      },
      timestamp: {
        title: "时间戳"
      },
      cron: {
        title: "Cron表达式"
      },
      curl: {
        title: "cURL转换"
      },
      "text-diff": {
        title: "文本对比"
      },
      "query-string": {
        title: "Query参数"
      },
      cookie: {
        title: "Cookie解析"
      }
    },
    toolInputPlaceholders: {
      json: "这里输入 JSON。",
      "url-encode": "这里输入完整 URL，或需要编码的 URL 参数值。",
      "text-codec": "这里输入普通文本，或 Unicode 转义文本。",
      base64: "这里输入普通文本，或 Base64 文本。",
      html: "这里输入 HTML 代码或 HTML Entity 文本。",
      timestamp: "这里输入时间戳或日期时间。",
      cron: "这里输入标准 5 段 Cron 表达式，例如 */15 9-18 * * 1-5。",
      curl: "这里输入完整 cURL 命令。",
      "text-diff": "这里输入原始文本。",
      "query-string": "这里输入 Query String，或逐行 key=value 文本。",
      cookie: "这里输入 Cookie 或 Set-Cookie 文本。"
    },
    modeInputPlaceholders: {
      "text-decode": "这里输入类似 \\u4F60\\u597D 的 Unicode 转义文本。",
      "base64-decode": "这里输入 Base64 文本。",
      "base64-image":
        "这里输入 Base64 图片，或 data:image/...;base64,... 格式的数据。",
      "entity-encode": "这里输入普通文本，输出 HTML Entity。",
      "entity-decode": "这里输入 HTML Entity 文本，例如 &lt;div&gt;。",
      "url-decode": "这里输入已经编码的 URL 或参数值。",
      "timestamp-convert": "这里输入秒级/毫秒级时间戳，或日期时间。",
      "cron-describe": "这里输入 5 段 Cron 表达式，例如 */15 9-18 * * 1-5。",
      "curl-to-fetch": "这里输入完整 cURL 命令。",
      "curl-to-axios": "这里输入完整 cURL 命令。",
      "curl-to-python": "这里输入完整 cURL 命令。",
      "curl-to-node": "这里输入完整 cURL 命令。",
      "curl-to-go": "这里输入完整 cURL 命令。",
      "curl-to-php": "这里输入完整 cURL 命令。",
      "query-parse": "这里输入带或不带 ? 的 Query String。",
      "query-build": "这里按行输入 key=value，每行一个参数。",
      "cookie-parse": "这里输入 Cookie 请求头内容，例如 a=1; b=2。",
      "set-cookie-parse": "这里输入一行或多行 Set-Cookie。"
    },
    secondaryInputPlaceholders: {
      "text-diff-compare": "这里输入修改后的文本。"
    },
    urlActions: {
      encode: "编码",
      decode: "解码"
    },
    timestampTool: {
      liveTitle: "当前时间戳",
      liveSummary: "基于当前浏览器时间实时更新，可直接用于接口调试和日志排查。",
      secondsLabel: "秒级时间戳",
      millisecondsLabel: "毫秒级时间戳",
      localTimeLabel: "本地时间",
      utcTimeLabel: "UTC 时间",
      timezoneLabel: "当前时区",
      fromTimestampTitle: "时间戳转时间",
      fromTimestampSummary: "输入秒级或毫秒级时间戳，立即转换为可读时间。",
      toTimestampTitle: "时间转时间戳",
      toTimestampSummary: "输入本地日期时间，立即输出秒级和毫秒级时间戳。",
      fillCurrentLabel: "填入当前时间戳",
      fillNowLabel: "填入当前时间",
      datetimePlaceholder: "例如 2026-03-30 21:12:25",
      unitAuto: "自动识别",
      unitSeconds: "秒 (s)",
      unitMilliseconds: "毫秒 (ms)",
      emptyHint: "输入后会在这里显示转换结果。"
    },
    cronTool: {
      title: "Cron 执行时间计算",
      summary: "切换表达式类型，输入 Cron 表达式后立即查看执行说明和接下来 7 次执行时间。",
      typeLabel: "类型",
      expressionLabel: "Cron 表达式",
      currentTimezoneLabel: "按当前时区计算",
      fieldGuideLabel: "字段格式",
      exampleLabel: "示例",
      descriptionLabel: "执行说明",
      nextRunsLabel: "接下来 7 次执行时间",
      emptyHint: "输入合法的 Cron 表达式后，会在这里显示执行说明和运行时间。",
      flavors: {
        linux: {
          title: "Linux",
          fieldGuide: "分 时 日 月 周",
          example: "0 */12 * * *",
          hint: "只输入 5 段核心字段，不要带 user 或 command。"
        },
        spring: {
          title: "Java(Spring)",
          fieldGuide: "秒 分 时 日 月 周",
          example: "0 0 */12 * * *",
          hint: "Spring 定时任务常用 6 段表达式，包含秒字段。"
        },
        quartz: {
          title: "Java(Quartz)",
          fieldGuide: "秒 分 时 日 月 周 [年]",
          example: "0 0 18 L * ?",
          hint: "Quartz 支持 ?、L、# 等规则，年字段可选。"
        }
      }
    },
    timestampResult: {
      detectedType: "识别类型",
      seconds: "秒级时间戳",
      milliseconds: "毫秒级时间戳",
      isoUtc: "UTC 时间",
      localTime: "本地时间",
      detectedTypes: {
        seconds: "秒级时间戳",
        milliseconds: "毫秒级时间戳",
        datetime: "日期时间"
      }
    },
    textDiff: {
      originalLabel: "原始文本",
      modifiedLabel: "修改后文本",
      resultLabel: "差异结果",
      summary: ({ added, removed, unchanged }) =>
        `新增 ${added} 行，删除 ${removed} 行，相同 ${unchanged} 行`,
      empty: "两边内容完全一致。"
    },
    jsonTree: {
      objectCount: (count) => `${count} 个字段`,
      arrayCount: (count) => `${count} 项`,
      emptyObject: "{}",
      emptyArray: "[]"
    },
    modes: {
      "json-format": {
        title: "格式化",
        summary: "解析并按缩进输出 JSON，便于阅读和排错。",
        actionLabel: "格式化 JSON"
      },
      "json-minify": {
        title: "压缩",
        summary: "移除多余空白，输出紧凑 JSON。",
        actionLabel: "压缩 JSON"
      },
      "json-escape": {
        title: "转义",
        summary: "把原始文本转换成 JSON 字符串片段。",
        actionLabel: "转义文本"
      },
      "json-unescape": {
        title: "去除转义",
        summary: "还原已经转义的 JSON 字符串内容。",
        actionLabel: "去除转义"
      },
      "text-encode": {
        title: "编码",
        summary: "把普通文本转换成 Unicode 转义文本，便于传输和排查。",
        actionLabel: "编码文本"
      },
      "text-decode": {
        title: "解码",
        summary: "把 Unicode 转义文本还原成普通文本。",
        actionLabel: "解码文本"
      },
      "base64-encode": {
        title: "编码",
        summary: "把普通文本按 UTF-8 编码后转换成 Base64 文本。",
        actionLabel: "编码 Base64"
      },
      "base64-decode": {
        title: "解码",
        summary: "把 Base64 文本还原成普通文本。",
        actionLabel: "解码 Base64"
      },
      "base64-image": {
        title: "图片",
        summary: "预览 Base64 图片，并提供下载。",
        actionLabel: "预览图片"
      },
      "entity-encode": {
        title: "编码",
        summary: "把文本中的 HTML 保留字符编码成 Entity。",
        actionLabel: "编码 Entity"
      },
      "entity-decode": {
        title: "解码",
        summary: "把 HTML Entity 还原成普通文本。",
        actionLabel: "解码 Entity"
      },
      "html-format": {
        title: "格式化",
        summary: "整理 HTML 缩进与层级，便于阅读和修改。",
        actionLabel: "格式化 HTML"
      },
      "html-preview": {
        title: "预览",
        summary: "在沙箱预览区即时渲染当前 HTML。",
        actionLabel: "预览 HTML"
      },
      "timestamp-convert": {
        title: "转换",
        summary: "自动识别秒级、毫秒级时间戳或日期时间，并输出全部结果。",
        actionLabel: "转换时间戳"
      },
      "cron-describe": {
        title: "解析",
        summary: "解析标准 5 段 Cron 表达式，并说明每一段的含义。",
        actionLabel: "解析 Cron"
      },
      "curl-to-fetch": {
        title: "转 Fetch",
        summary: "把 cURL 命令转换成 fetch 代码。",
        actionLabel: "转换为 Fetch"
      },
      "curl-to-axios": {
        title: "转 Axios",
        summary: "把 cURL 命令转换成 axios 代码。",
        actionLabel: "转换为 Axios"
      },
      "curl-to-python": {
        title: "转 Python",
        summary: "把 cURL 命令转换成 Python requests 代码。",
        actionLabel: "转换为 Python"
      },
      "curl-to-node": {
        title: "转 Node.js",
        summary: "把 cURL 命令转换成 Node.js 原生 http/https 代码。",
        actionLabel: "转换为 Node.js"
      },
      "curl-to-go": {
        title: "转 Go",
        summary: "把 cURL 命令转换成 Go net/http 代码。",
        actionLabel: "转换为 Go"
      },
      "curl-to-php": {
        title: "转 PHP",
        summary: "把 cURL 命令转换成 PHP cURL 代码。",
        actionLabel: "转换为 PHP"
      },
      "text-diff-compare": {
        title: "对比",
        summary: "对比两段文本的行级差异。",
        actionLabel: "对比文本"
      },
      "query-parse": {
        title: "解析",
        summary: "把 Query String 解析成有序 JSON 数组，保留重复 key 和空值。",
        actionLabel: "解析 Query"
      },
      "query-build": {
        title: "组装",
        summary: "把逐行 key=value 文本组装成 Query String。",
        actionLabel: "组装 Query"
      },
      "cookie-parse": {
        title: "Cookie",
        summary: "解析请求头中的 Cookie 字符串。",
        actionLabel: "解析 Cookie"
      },
      "set-cookie-parse": {
        title: "Set-Cookie",
        summary: "解析一行或多行 Set-Cookie，并展开常见属性。",
        actionLabel: "解析 Set-Cookie"
      },
      "url-safe": {
        title: "安全模式",
        summary: "保留 URL 结构字符，适合编码完整 URL。",
        actionLabel: "安全编码"
      },
      "url-full": {
        title: "完全模式",
        summary: "编码所有需要转义的字符，适合参数值。",
        actionLabel: "完全编码"
      },
      "url-decode": {
        title: "解码",
        summary: "还原已经编码的 URL 或参数值。",
        actionLabel: "解码 URL"
      }
    }
  },
  en: {
    toolsLabel: "Tools",
    modesLabel: "Modes",
    urlEncodeModesLabel: "Encode mode",
    feedbackContactLabel: "Feedback / suggestions",
    officialLinks: {
      justOneApi: {
        title: "Just One API",
        summary: "Social Media & Ecommerce APIs"
      },
      justSerpApi: {
        title: "Just Serp API",
        summary: "Google Search API"
      }
    },
    clearLabel: "Clear",
    copyLabel: "Copy",
    actionFeedback: {
      cleared: "Cleared",
      copied: "Copied to clipboard",
      nothingToCopy: "Nothing to copy",
      copyFailed: "Copy failed"
    },
    outputPlaceholder: "Run a mode and the result will appear here.",
    statusIdle:
      "Pick a tool and mode, then run it. Everything stays in the browser.",
    statusRunning: (actionLabel) => `Running: ${actionLabel}`,
    errorMessages: {
      INVALID_JSON: "The input is not valid JSON. Check brackets, quotes, and commas.",
      INVALID_ESCAPED_JSON: "The input is not a valid escaped JSON string fragment.",
      INVALID_CRON_EXPRESSION: "The input is not a valid cron expression.",
      INVALID_CURL_COMMAND: "The input is not a valid cURL command.",
      INVALID_JWT: "The input is not a valid JWT.",
      INVALID_TIMESTAMP: "The input is not a valid timestamp or datetime value.",
      INVALID_TEXT_ENCODED_TEXT: "The input is not valid Unicode-escaped text.",
      INVALID_HEX_TEXT: "The input is not valid hex text.",
      INVALID_BASE64_TEXT: "The input is not valid Base64 text.",
      INVALID_BASE64_IMAGE: "The input is not valid Base64 image data.",
      INVALID_URL_ENCODED_TEXT: "The input is not valid URL-encoded text.",
      UNKNOWN: "Processing failed. Check the current input."
    },
    tools: {
      json: {
        title: "JSON"
      },
      "url-encode": {
        title: "URL Encode"
      },
      "text-codec": {
        title: "Text Codec"
      },
      base64: {
        title: "Base64"
      },
      html: {
        title: "HTML"
      },
      timestamp: {
        title: "Timestamp"
      },
      cron: {
        title: "Cron"
      },
      curl: {
        title: "cURL"
      },
      "text-diff": {
        title: "Text Diff"
      },
      "query-string": {
        title: "Query String"
      },
      cookie: {
        title: "Cookie"
      }
    },
    toolInputPlaceholders: {
      json: "Enter JSON here.",
      "url-encode":
        "Enter a full URL here, or a URL component value that needs encoding.",
      "text-codec": "Enter plain text or Unicode-escaped text here.",
      base64: "Enter plain text or Base64 text here.",
      html: "Enter HTML or HTML entity text here.",
      timestamp: "Enter a timestamp or datetime value here.",
      cron: "Enter a standard 5-field cron expression here, for example */15 9-18 * * 1-5.",
      curl: "Enter a complete cURL command here.",
      "text-diff": "Enter the original text here.",
      "query-string": "Enter a query string or line-based key=value pairs here.",
      cookie: "Enter Cookie or Set-Cookie text here."
    },
    modeInputPlaceholders: {
      "text-decode":
        "Enter Unicode-escaped text here, for example \\u4F60\\u597D.",
      "base64-decode": "Enter Base64 text here.",
      "base64-image":
        "Enter Base64 image data or a data:image/...;base64,... URL here.",
      "entity-encode": "Enter plain text here and encode reserved HTML characters.",
      "entity-decode":
        "Enter HTML entity text here, for example &lt;div&gt;.",
      "url-decode": "Enter an encoded URL or encoded URL component here.",
      "timestamp-convert":
        "Enter a seconds or milliseconds timestamp, or a datetime value.",
      "cron-describe":
        "Enter a 5-field cron expression, for example */15 9-18 * * 1-5.",
      "curl-to-fetch": "Enter a complete cURL command here.",
      "curl-to-axios": "Enter a complete cURL command here.",
      "curl-to-python": "Enter a complete cURL command here.",
      "curl-to-node": "Enter a complete cURL command here.",
      "curl-to-go": "Enter a complete cURL command here.",
      "curl-to-php": "Enter a complete cURL command here.",
      "query-parse": "Enter a query string here, with or without a leading ?.",
      "query-build": "Enter one key=value pair per line.",
      "cookie-parse": "Enter a Cookie header string here, for example a=1; b=2.",
      "set-cookie-parse": "Enter one or more Set-Cookie lines here."
    },
    secondaryInputPlaceholders: {
      "text-diff-compare": "Enter the modified text here."
    },
    urlActions: {
      encode: "Encode",
      decode: "Decode"
    },
    timestampTool: {
      liveTitle: "Current Timestamp",
      liveSummary:
        "Updates in real time using the browser clock for quick request and log debugging.",
      secondsLabel: "Seconds timestamp",
      millisecondsLabel: "Milliseconds timestamp",
      localTimeLabel: "Local time",
      utcTimeLabel: "UTC time",
      timezoneLabel: "Time zone",
      fromTimestampTitle: "Timestamp to Time",
      fromTimestampSummary:
        "Enter a seconds or milliseconds timestamp and convert it instantly.",
      toTimestampTitle: "Time to Timestamp",
      toTimestampSummary:
        "Enter a local datetime and get both seconds and milliseconds timestamps.",
      fillCurrentLabel: "Use current timestamp",
      fillNowLabel: "Use current time",
      datetimePlaceholder: "For example 2026-03-30 21:12:25",
      unitAuto: "Auto detect",
      unitSeconds: "Seconds (s)",
      unitMilliseconds: "Milliseconds (ms)",
      emptyHint: "Converted values will appear here."
    },
    cronTool: {
      title: "Cron Schedule Calculator",
      summary:
        "Switch cron flavors, enter an expression, and review both the human description and the next 7 execution times.",
      typeLabel: "Type",
      expressionLabel: "Cron Expression",
      currentTimezoneLabel: "Using current time zone",
      fieldGuideLabel: "Field Guide",
      exampleLabel: "Example",
      descriptionLabel: "Execution Summary",
      nextRunsLabel: "Next 7 Execution Times",
      emptyHint:
        "Enter a valid cron expression to see the human description and upcoming run times.",
      flavors: {
        linux: {
          title: "Linux",
          fieldGuide: "minute hour day month weekday",
          example: "0 */12 * * *",
          hint: "Only enter the 5 core fields, without user or command parts."
        },
        spring: {
          title: "Java(Spring)",
          fieldGuide: "second minute hour day month weekday",
          example: "0 0 */12 * * *",
          hint: "Spring schedules usually use 6 fields and include seconds."
        },
        quartz: {
          title: "Java(Quartz)",
          fieldGuide: "second minute hour day month weekday [year]",
          example: "0 0 18 L * ?",
          hint: "Quartz supports ?, L, #, and an optional year field."
        }
      }
    },
    timestampResult: {
      detectedType: "Detected Type",
      seconds: "Seconds",
      milliseconds: "Milliseconds",
      isoUtc: "UTC Time",
      localTime: "Local Time",
      detectedTypes: {
        seconds: "Seconds timestamp",
        milliseconds: "Milliseconds timestamp",
        datetime: "Datetime"
      }
    },
    textDiff: {
      originalLabel: "Original Text",
      modifiedLabel: "Modified Text",
      resultLabel: "Diff Result",
      summary: ({ added, removed, unchanged }) =>
        `${added} added, ${removed} removed, ${unchanged} unchanged`,
      empty: "Both sides are identical."
    },
    jsonTree: {
      objectCount: (count) => `${count} keys`,
      arrayCount: (count) => `${count} items`,
      emptyObject: "{}",
      emptyArray: "[]"
    },
    modes: {
      "json-format": {
        title: "Format",
        summary: "Parse and pretty-print JSON for easier reading and debugging.",
        actionLabel: "Format JSON"
      },
      "json-minify": {
        title: "Minify",
        summary: "Remove whitespace and output compact JSON.",
        actionLabel: "Minify JSON"
      },
      "json-escape": {
        title: "Escape",
        summary: "Turn raw text into a JSON-safe string fragment.",
        actionLabel: "Escape text"
      },
      "json-unescape": {
        title: "Unescape",
        summary: "Restore escaped JSON string content back to plain text.",
        actionLabel: "Unescape text"
      },
      "text-encode": {
        title: "Encode",
        summary: "Convert plain text into Unicode escape sequences.",
        actionLabel: "Encode text"
      },
      "text-decode": {
        title: "Decode",
        summary: "Turn Unicode escape sequences back into plain text.",
        actionLabel: "Decode text"
      },
      "base64-encode": {
        title: "Encode",
        summary: "Encode plain text as UTF-8 Base64 text.",
        actionLabel: "Encode Base64"
      },
      "base64-decode": {
        title: "Decode",
        summary: "Decode Base64 text back into plain text.",
        actionLabel: "Decode Base64"
      },
      "base64-image": {
        title: "Image",
        summary: "Preview a Base64 image and download it.",
        actionLabel: "Preview image"
      },
      "entity-encode": {
        title: "Encode",
        summary: "Encode reserved HTML characters as entities.",
        actionLabel: "Encode entities"
      },
      "entity-decode": {
        title: "Decode",
        summary: "Decode HTML entities back into plain text.",
        actionLabel: "Decode entities"
      },
      "html-format": {
        title: "Format",
        summary: "Re-indent HTML so the structure is easier to read and edit.",
        actionLabel: "Format HTML"
      },
      "html-preview": {
        title: "Preview",
        summary: "Render the current HTML instantly in a sandboxed preview pane.",
        actionLabel: "Preview HTML"
      },
      "timestamp-convert": {
        title: "Convert",
        summary: "Auto-detect timestamps and datetimes, then show every useful result.",
        actionLabel: "Convert timestamp"
      },
      "cron-describe": {
        title: "Describe",
        summary: "Parse a standard 5-field cron expression and explain each field.",
        actionLabel: "Describe cron"
      },
      "curl-to-fetch": {
        title: "To Fetch",
        summary: "Convert a cURL command into fetch code.",
        actionLabel: "Convert to fetch"
      },
      "curl-to-axios": {
        title: "To Axios",
        summary: "Convert a cURL command into axios code.",
        actionLabel: "Convert to axios"
      },
      "curl-to-python": {
        title: "To Python",
        summary: "Convert a cURL command into Python requests code.",
        actionLabel: "Convert to Python"
      },
      "curl-to-node": {
        title: "To Node.js",
        summary: "Convert a cURL command into native Node.js http/https code.",
        actionLabel: "Convert to Node.js"
      },
      "curl-to-go": {
        title: "To Go",
        summary: "Convert a cURL command into Go net/http code.",
        actionLabel: "Convert to Go"
      },
      "curl-to-php": {
        title: "To PHP",
        summary: "Convert a cURL command into PHP cURL code.",
        actionLabel: "Convert to PHP"
      },
      "text-diff-compare": {
        title: "Compare",
        summary: "Compare two texts line by line.",
        actionLabel: "Compare text"
      },
      "query-parse": {
        title: "Parse",
        summary: "Parse a query string into an ordered JSON array and preserve repeated keys.",
        actionLabel: "Parse query"
      },
      "query-build": {
        title: "Build",
        summary: "Build a query string from one key=value pair per line.",
        actionLabel: "Build query"
      },
      "cookie-parse": {
        title: "Cookie",
        summary: "Parse a request Cookie header into ordered name/value pairs.",
        actionLabel: "Parse Cookie"
      },
      "set-cookie-parse": {
        title: "Set-Cookie",
        summary: "Parse one or more Set-Cookie lines and expand common attributes.",
        actionLabel: "Parse Set-Cookie"
      },
      "url-safe": {
        title: "Safe mode",
        summary: "Preserve URL structure characters. Best for full URLs.",
        actionLabel: "Encode URL"
      },
      "url-full": {
        title: "Full mode",
        summary: "Encode every reserved character. Best for parameter values.",
        actionLabel: "Encode component"
      },
      "url-decode": {
        title: "Decode",
        summary: "Restore encoded URLs or parameter values back to plain text.",
        actionLabel: "Decode URL"
      }
    }
  }
};
